<template>
  <div>
    <b-row class="mb-1">
      <b-col cols="12" md="6" lg="4" class="mb-2">
        <div class="d-flex align-items-center">
          <date-picker
            placeholder="Từ ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.fromDate"
            :disabled="isProcessing"
          ></date-picker>
          <span class="mx-1"></span>
          <date-picker
            placeholder="Đến ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.toDate"
            :disabled="isProcessing"
          ></date-picker>
        </div>
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-input
          size="sm"
          v-model="filter.code"
          placeholder="Mã coupon"
          :disabled="isProcessing"
          @keyup.enter="onFilter"
        />
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-select
          size="sm"
          v-model="filter.storeId"
          :options="storeOptions"
          value-field="id"
          text-field="name"
          :disabled="isProcessing"
        />
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-input
          size="sm"
          v-model="filter.billNumber"
          placeholder="Số hóa đơn"
          :disabled="isProcessing"
          @keyup.enter="onFilter"
        />
      </b-col>
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-select
          size="sm"
          v-model="filter.delta"
          :options="typeOptions"
          value-field="id"
          text-field="name"
          :disabled="isProcessing"
        />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="12">
        <b-button
          style="font-weight: 600; width: 70px"
          variant="primary"
          size="sm"
          :disabled="isProcessing"
          @click="onFilter"
          >Lọc</b-button
        >
      </b-col>
    </b-row>

    <b-table
      :items="items"
      :fields="fields"
      :busy="onLoading"
      bordered
      hover
      small
      responsive
      show-empty
    >
      <template #table-busy>
        <div class="text-center py-3"><b-spinner small variant="primary" /></div>
      </template>
      <template #empty>
        <p class="text-center text-muted mb-0">{{ emptyText }}</p>
      </template>
      <template #cell(createdAt)="{ value }">
        {{ formatTime(value) }}
      </template>
      <template #cell(code)="{ value }">
        <span class="text-break">{{ value }}</span>
      </template>
      <template #cell(typeName)="{ item }">
        <span
          :class="[
            'label label-inline font-weight-bold',
            item.delta > 0 ? 'label-light-success' : 'label-light-warning',
          ]"
          >{{ item.typeName }}</span
        >
      </template>
      <template #cell(billNumber)="{ item, value }">
        <a
          v-if="value"
          :href="billLink(item)"
          target="_blank"
          rel="noopener"
          >{{ value }}</a
        >
        <span v-else>—</span>
      </template>
      <template #cell(usage)="{ item }">
        {{ item.usedAfter }} / {{ item.maxUse === null ? '—' : item.maxUse }}
      </template>
    </b-table>

    <b-pagination
      v-if="totalPage > 1"
      v-model="page"
      :total-rows="totalRow"
      :per-page="limit"
      :disabled="isProcessing"
      size="sm"
      align="right"
      @change="onChangePage"
    ></b-pagination>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import timeUtils, { formatSpecificDate } from '@/utils/date';
import { makeToastFaile } from '@/utils/common';
import { BILL_TYPE } from '@/utils/enum';
import fileDownload from '@/utils/file-download';
import submitLock from '@/view/mixins/submit-lock.mixins';
import {
  COUPON_USAGE_TYPE_OPTIONS,
  COUPON_HISTORY_MESSAGES,
} from '@/utils/constants';
import { showHistoryError } from '@/view/components/coupon/coupon-history-helpers';

const PAGE_LIMIT = 20;

export default {
  name: 'CouponUsageLogTable',
  mixins: [submitLock],
  props: {
    couponProgramId: { type: [Number, String], required: true },
    // Bấm số "Đã sử dụng" của 1 mã → lọc sẵn theo mã đó
    initialCode: { type: String, default: '' },
  },
  data() {
    return {
      dpConfigs: timeUtils.DP_CONFIG,
      typeOptions: COUPON_USAGE_TYPE_OPTIONS,
      filter: {
        fromDate: '',
        toDate: '',
        code: this.initialCode || '',
        storeId: null,
        billNumber: '',
        delta: null,
      },
      appliedParams: {},
      storeOptions: [{ id: null, name: 'Tất cả cửa hàng' }],
      items: [],
      page: 1,
      limit: PAGE_LIMIT,
      totalRow: 0,
      totalPage: 0,
      onLoading: false,
      isFiltered: Boolean(this.initialCode),
    };
  },
  computed: {
    // Đang tải hoặc đang xuất thì khóa cả nhóm lọc / phân trang / xuất
    isProcessing() {
      return this.onLoading || this.isSubmitting('export');
    },
    emptyText() {
      return this.isFiltered
        ? COUPON_HISTORY_MESSAGES.NO_MATCH
        : COUPON_HISTORY_MESSAGES.NO_USAGE;
    },
    fields() {
      return [
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '150px' } },
        { key: 'code', label: 'Mã coupon' },
        { key: 'typeName', label: 'Loại' },
        { key: 'billNumber', label: 'Số hóa đơn' },
        { key: 'storeName', label: 'Cửa hàng' },
        { key: 'usage', label: 'Lượt đã dùng / Tối đa' },
        { key: 'createdByName', label: 'Người thực hiện' },
      ];
    },
  },
  watch: {
    // Màn chi tiết đặt nút Xuất Excel trên tiêu đề khung → cần biết lúc nào phải khóa nút
    isProcessing: {
      immediate: true,
      handler(processing) {
        this.$emit('state-change', {
          processing,
          exporting: this.isSubmitting('export'),
        });
      },
    },
    // Tab đã mở mà bấm số "Đã sử dụng" của mã khác → lọc lại theo mã mới
    initialCode(code) {
      this.filter.code = code || '';
      this.onFilter();
    },
  },
  mounted() {
    this.fetchStores();
    this.appliedParams = this.buildParams();
    this.fetchData();
  },
  methods: {
    billLink(item) {
      // Router hash mode; phiếu trả và hóa đơn sỉ mở màn riêng
      let path = '#/bills/update-bill';
      if (item.isReturnBill) {
        path = '#/returnBills/update-return-bill';
      } else if (item.billType === BILL_TYPE.WHOLESALE_INVOICE) {
        path = '#/wholesaleInvoices/update-wholesale-invoice';
      }
      return `${path}?id=${item.billNumber}`;
    },
    formatTime(value) {
      return value ? moment(value).format('HH:mm DD/MM/YYYY') : '';
    },
    toApiDate(value) {
      return value ? formatSpecificDate(value, 'DD/MM/YYYY', 'YYYY-MM-DD') : null;
    },
    buildParams() {
      return {
        couponProgramId: this.couponProgramId,
        code: this.filter.code.trim() || null,
        storeId: this.filter.storeId,
        billNumber: String(this.filter.billNumber || '').trim() || null,
        delta: this.filter.delta,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
    },
    fetchStores() {
      ApiService.get('stores?limit=1000&page=1')
        .then((response) => {
          const stores = response.data.data.list_store || [];
          this.storeOptions = [
            { id: null, name: 'Tất cả cửa hàng' },
            ...stores.map((store) => ({ id: store.id, name: store.name })),
          ];
        })
        .catch(() => {});
    },
    onFilter() {
      if (this.isProcessing) return;
      const params = this.buildParams();
      if (params.fromDate && params.toDate && params.fromDate > params.toDate) {
        makeToastFaile(COUPON_HISTORY_MESSAGES.INVALID_DATE_RANGE);
        return;
      }
      this.appliedParams = params;
      this.isFiltered = Boolean(
        params.code || params.storeId || params.billNumber || params.delta,
      );
      this.page = 1;
      this.fetchData();
    },
    onChangePage(page) {
      this.page = page;
      this.fetchData();
    },
    async fetchData() {
      if (this.onLoading) return;
      this.onLoading = true;
      try {
        const { data } = await ApiService.query('v2/coupon-usage-logs', {
          params: { ...this.appliedParams, page: this.page, limit: this.limit },
        });
        this.items = data.data.dataset;
        this.totalRow = data.data.totalRow;
        this.totalPage = data.data.totalPage;
      } catch (error) {
        showHistoryError(error);
      } finally {
        this.onLoading = false;
      }
    },
    /** Gọi từ nút Xuất Excel trên tiêu đề khung Lịch sử (Update.vue), theo bộ lọc đã áp dụng. */
    exportExcel() {
      if (this.isProcessing) return;
      return this.withSubmitLock('export', async () => {
        try {
          const response = await ApiService.query('v2/coupon-usage-logs/export', {
            params: this.appliedParams,
            responseType: 'blob',
          });
          fileDownload.createFile(
            response.data,
            `lich-su-su-dung-coupon-${this.couponProgramId}-${moment().format('YYYYMMDD')}.xlsx`,
          );
        } catch (error) {
          await showHistoryError(error);
        }
      });
    },
  },
};
</script>
